import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../config';
import { ensureRcWidgetLoaded } from './shared/rcWidget';
import './SharedNav.css';

const PAGE_TITLES = {
  '/': 'Recruiter Portal',
  '/dashboard': 'CRM Dashboard',
  '/recruiter-portal': 'Recruiter Portal',
  '/tasks': 'Task Inbox',
  '/fadv-activity': 'FA Activity',
  '/reports': 'Reports',
  '/my-clients': 'My Clients',
  '/users': 'User Management',
  '/logs': 'Logs',
  '/candidate-archive': 'Candidate Archive',
  '/prior-candidates': 'Prior Candidates',
  '/company-pages': 'Company Pages',
  '/accounts': 'Accounts',
  '/books': 'Books',
  '/playbook': 'SOP',
  '/fa-knowledge': 'FA Knowledge',
  '/station-map': 'Station Map',
  '/proposals': 'Proposals',
  '/onboarding': 'Onboarding',
  '/onboarding-table': 'Onboarding Table',
  '/inbound': 'Inbound',
  '/linehaul': 'Linehaul',
  '/client-interviews': 'Ready for Interview',
  '/blog-agent': 'Blog Agent',
  '/message-templates': 'Message Templates',
  '/intro-texts': 'Intro Texts',
  '/messages': 'Messages',
  '/resources': 'Resources',
  '/ai-lab': 'Mountain AI Lab',
};

const DashboardIcon = () => (
  <svg viewBox="0 0 16 16" width="13" height="13" fill="currentColor" style={{ flexShrink: 0 }}>
    <rect x="0" y="0" width="7" height="7" rx="1.5" />
    <rect x="9" y="0" width="7" height="7" rx="1.5" />
    <rect x="0" y="9" width="7" height="7" rx="1.5" />
    <rect x="9" y="9" width="7" height="7" rx="1.5" />
  </svg>
);

// Backs both the reorganized dropdowns and the ⌘K search palette -- one list, one source of
// truth for every destination in the app, so the two navigation surfaces can't drift apart.
const NAV_ITEMS = [
  { name: 'Recruiter Portal', icon: '👥', path: '/recruiter-portal', group: 'Primary' },
  { name: 'Tasks', icon: '✅', path: '/tasks', group: 'Primary' },
  { name: 'Messages', icon: '💬', path: '/messages', group: 'Primary' },
  { name: 'Accounts', icon: '💼', path: '/accounts', group: 'Primary' },
  { name: 'Onboarding', icon: '📋', path: '/onboarding', group: 'Clients' },
  { name: 'Onboarding Table', icon: '📊', path: '/onboarding-table', group: 'Clients' },
  { name: 'Proposals', icon: '📄', path: '/proposals', group: 'Clients', adminOnly: true },
  { name: 'Inbound', icon: '📥', path: '/inbound', group: 'Clients', adminOnly: true },
  { name: 'Linehaul', icon: '🚛', path: '/linehaul', group: 'Clients', adminOnly: true },
  { name: 'Ready for Interview', icon: '✅', path: '/client-interviews', group: 'Candidates' },
  { name: 'Candidate Archive', icon: '🗃', path: '/candidate-archive', group: 'Candidates' },
  { name: 'Prior Candidates', icon: '📇', path: '/prior-candidates', group: 'Candidates' },
  { name: 'FA Knowledge', icon: '🗂', path: '/fa-knowledge', group: 'Candidates · Reference' },
  { name: 'FA Activity', icon: '📨', path: '/fadv-activity', group: 'Comms & Reports' },
  { name: 'Maps', icon: '🗺', path: '/station-map', group: 'Comms & Reports' },
  { name: 'Reports', icon: '📈', path: '/reports', group: 'Comms & Reports' },
  { name: 'Message Templates', icon: '✉️', path: '/message-templates', group: 'Comms & Reports' },
  { name: 'Intro Texts', icon: '📲', path: '/intro-texts', group: 'Comms & Reports' },
  { name: 'Resources', icon: '📚', path: '/resources', group: 'Comms & Reports' },
  { name: 'Mountain AI Lab', icon: '🧪', path: '/ai-lab', group: 'Comms & Reports' },
  { name: 'Manage Users', icon: '👥', path: '/users', group: 'Comms & Reports', adminOnly: true, alsoRecruitingAdmin: true },
  { name: 'Blog Agent', icon: '✍️', path: '/blog-agent', group: 'Comms & Reports', adminOnly: true },
  { name: 'Books', icon: '📒', path: '/books', group: 'Comms & Reports', adminOnly: true, booksOnly: true },
  // Deprecated -- admin-only, being phased out. Consolidated here from wherever they used to
  // live (Clients dropdown, Candidates dropdown, Candidates·Reference) so there's one place to
  // find old pages instead of them being scattered across menus that imply they're still current.
  { name: 'My Clients', icon: '📞', path: '/my-clients', group: 'Deprecated', adminOnly: true },
  { name: 'Company Pages', icon: '🏢', path: '/company-pages', group: 'Deprecated', adminOnly: true },
  { name: 'CRM Dashboard', icon: '▦', path: '/dashboard', group: 'Deprecated', adminOnly: true },
  { name: 'SOP', icon: '📝', path: '/playbook', group: 'Deprecated', adminOnly: true },
  { name: 'Logs', icon: '📊', path: '/logs', group: 'Deprecated', adminOnly: true },
];

export default function SharedNav({ user, onLogout, taskCountOverride }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [openDropdown, setOpenDropdown] = useState(null);
  const [taskCount, setTaskCount] = useState(0);
  const [callsLeftToday, setCallsLeftToday] = useState(0);
  const [introSmsCount, setIntroSmsCount] = useState(0);
  const [messagesUnreadCount, setMessagesUnreadCount] = useState(0);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [paletteQuery, setPaletteQuery] = useState('');
  const navRef = useRef(null);
  const paletteInputRef = useRef(null);

  const isAdmin = user?.role?.toLowerCase() === 'admin';
  // Books (October 2026): AJ and Monica only, mirrors BOOKS_EMAILS in routes/books.js.
  const canBooks = ['ajroy@mountainrecruiting.com', 'monica@mountainrecruiting.com'].includes(String(user?.email || '').toLowerCase());
  const isRecruitingAdmin = user?.role?.toLowerCase() === 'recruiting_admin';
  // /resources (and potentially other paths) 301-redirect to a trailing slash --
  // client/public/resources/ is a real directory on disk (serves PDFs/HTML directly),
  // so Express's static middleware appends the slash before the SPA catch-all ever
  // sees the request, permanently changing the pathname react-router observes.
  const normalizedPathname = pathname.length > 1 ? pathname.replace(/\/$/, '') : pathname;
  const pageTitle = PAGE_TITLES[normalizedPathname] || 'Mountain Recruiting';
  const displayCount = taskCountOverride !== undefined ? taskCountOverride : taskCount;

  useEffect(() => {
    if (taskCountOverride !== undefined) return; // skip fetch when override is active
    const fetchCount = async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${API_URL}/api/tasks/count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTaskCount(r.data.open || 0);
      } catch (e) { /* ignore */ }
    };
    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    return () => clearInterval(interval);
  }, [pathname, taskCountOverride]);

  // Calls left on today's call list (callbacks + new uploads), shown on Recruiter Portal (October 2026)
  useEffect(() => {
    const fetchToday = async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${API_URL}/api/candidates/today`, { headers: { Authorization: `Bearer ${token}` } });
        setCallsLeftToday((r.data.callbacks?.length || 0) + (r.data.newUploads?.length || 0));
      } catch (e) { /* ignore */ }
    };
    fetchToday();
    const interval = setInterval(fetchToday, 120000);
    return () => clearInterval(interval);
  }, [pathname]);

  useEffect(() => {
    const fetchIntroSmsCount = async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${API_URL}/api/intro-sms/count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIntroSmsCount(r.data.count || 0);
      } catch (e) { /* ignore */ }
    };
    fetchIntroSmsCount();
    const interval = setInterval(fetchIntroSmsCount, 60000);
    return () => clearInterval(interval);
  }, [pathname]);

  useEffect(() => {
    const fetchMessagesCount = async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${API_URL}/api/sms-inbox/count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMessagesUnreadCount(r.data.count || 0);
      } catch (e) { /* ignore */ }
    };
    fetchMessagesCount();
    const interval = setInterval(fetchMessagesCount, 60000);
    return () => clearInterval(interval);
  }, [pathname]);

  // Click-to-call widget -- mounted once, globally, on whichever page loads first (SharedNav
  // renders on every internal page). ensureRcWidgetLoaded() self-guards against double-injection
  // across route changes/remounts and is synchronous/dependency-free (Auth Code + PKCE, no JWT
  // fetch) -- deliberately kept this simple since SharedNav is the one component every
  // admin/recruiter page depends on. try/catch here is redundant with the one already inside
  // ensureRcWidgetLoaded(), kept anyway so a future change to that function can't turn into an
  // uncaught error inside this effect.
  useEffect(() => {
    try {
      if (isAdmin || user?.role?.toLowerCase() === 'recruiter' || isRecruitingAdmin) {
        ensureRcWidgetLoaded();
      }
    } catch (err) {
      console.error('[SharedNav] widget mount failed:', err.message);
    }
  }, [isAdmin, isRecruitingAdmin, user]);

  useEffect(() => {
    const handler = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const go = (path) => { navigate(path); setOpenDropdown(null); };

  const openPalette = useCallback(() => {
    setPaletteQuery('');
    setPaletteOpen(true);
    setOpenDropdown(null);
    setTimeout(() => paletteInputRef.current?.focus(), 10);
  }, []);
  const closePalette = useCallback(() => setPaletteOpen(false), []);

  // Global ⌘K / Ctrl+K to open the palette from anywhere, Escape to close -- standard
  // command-palette convention (Linear/Notion/GitHub), not something users have to discover
  // via a button.
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        openPalette();
      }
      if (e.key === 'Escape') closePalette();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [openPalette, closePalette]);

  const paletteResults = useMemo(() => {
    const q = paletteQuery.trim().toLowerCase();
    return NAV_ITEMS
      .filter(item => !item.adminOnly || isAdmin || (item.alsoRecruitingAdmin && isRecruitingAdmin))
      .filter(item => !item.booksOnly || canBooks)
      .filter(item => item.name.toLowerCase().includes(q));
  }, [paletteQuery, isAdmin, isRecruitingAdmin, canBooks]);

  const choosePaletteItem = (path) => {
    closePalette();
    go(path);
  };

  return (
    <header className="shared-nav" ref={navRef}>
      <div className="shared-nav-left">
        <button className="shared-nav-logo" title="Go to Recruiter Portal" onClick={() => go('/recruiter-portal')}>M</button>
        <div className="shared-nav-title-wrap">
          <div className="shared-nav-title-divider"></div>
          <h1 className="shared-nav-title">{pageTitle}</h1>
        </div>
      </div>

      <div className="shared-nav-right">
        <button className="nav-search-btn" onClick={openPalette}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
          Search pages
          <span className="nav-search-kbd">⌘K</span>
        </button>

        <button
          className={`shared-nav-btn${pathname === '/recruiter-portal' || pathname === '/' ? ' shared-nav-btn--active' : ''}`}
          onClick={() => go('/recruiter-portal')}
          style={{ position: 'relative' }}
          title={callsLeftToday > 0 ? `${callsLeftToday} on today's call list` : undefined}
        >
          👥 Recruiter Portal
          {callsLeftToday > 0 && (
            <span className="shared-nav-task-badge">{callsLeftToday > 99 ? '99+' : callsLeftToday}</span>
          )}
        </button>

        <button
          className={`shared-nav-btn${pathname === '/tasks' ? ' shared-nav-btn--active' : ''}`}
          onClick={() => go('/tasks')}
          style={{ position: 'relative' }}
        >
          ✅ Tasks
          {displayCount > 0 && (
            <span className="shared-nav-task-badge">{displayCount > 99 ? '99+' : displayCount}</span>
          )}
        </button>

        <button
          className={`shared-nav-btn${pathname === '/messages' ? ' shared-nav-btn--active' : ''}`}
          onClick={() => go('/messages')}
          style={{ position: 'relative' }}
        >
          💬 Messages
          {messagesUnreadCount > 0 && (
            <span className="shared-nav-task-badge">{messagesUnreadCount > 99 ? '99+' : messagesUnreadCount}</span>
          )}
        </button>

        <button
          className={`shared-nav-btn${pathname === '/accounts' ? ' shared-nav-btn--active' : ''}`}
          onClick={() => go('/accounts')}
        >
          💼 Accounts
        </button>

        {/* Clients */}
        <div className="shared-nav-dd-wrap">
          <button
            className={`shared-nav-btn shared-nav-btn--ghost${openDropdown === 'clients' ? ' shared-nav-btn--open' : ''}`}
            onClick={() => setOpenDropdown(v => v === 'clients' ? null : 'clients')}
          >
            Clients ▾
          </button>
          {openDropdown === 'clients' && (
            <div className="shared-nav-dropdown">
              <button className="shared-nav-dd-item" onClick={() => go('/onboarding')}>📋 Onboarding</button>
              <button className="shared-nav-dd-item" onClick={() => go('/onboarding-table')}>📊 Onboarding Table</button>
              {isAdmin && (
                <>
                  <div className="shared-nav-dd-divider"></div>
                  <div className="shared-nav-dd-section-label">Admin</div>
                  <button className="shared-nav-dd-item" onClick={() => go('/proposals')}>
                    📄 Proposals <span className="shared-nav-admin-dot" />
                  </button>
                  <button className="shared-nav-dd-item" onClick={() => go('/inbound')}>
                    📥 Inbound <span className="shared-nav-admin-dot" />
                  </button>
                  <button className="shared-nav-dd-item" onClick={() => go('/linehaul')}>
                    🚛 Linehaul <span className="shared-nav-admin-dot" />
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Candidates */}
        <div className="shared-nav-dd-wrap">
          <button
            className={`shared-nav-btn shared-nav-btn--ghost${openDropdown === 'candidates' ? ' shared-nav-btn--open' : ''}`}
            onClick={() => setOpenDropdown(v => v === 'candidates' ? null : 'candidates')}
          >
            Candidates ▾
          </button>
          {openDropdown === 'candidates' && (
            <div className="shared-nav-dropdown">
              <button className="shared-nav-dd-item" onClick={() => go('/client-interviews')}>✅ Ready for Interview</button>
              <button className="shared-nav-dd-item" onClick={() => go('/candidate-archive')}>🗃 Candidate Archive</button>
              <button className="shared-nav-dd-item" onClick={() => go('/prior-candidates')}>📇 Prior Candidates</button>
              <button className="shared-nav-dd-item" onClick={() => go('/fa-knowledge')}>🗂 FA Knowledge</button>
            </div>
          )}
        </div>

        {/* Comms & Reports */}
        <div className="shared-nav-dd-wrap">
          <button
            className={`shared-nav-btn shared-nav-btn--ghost${openDropdown === 'comms' ? ' shared-nav-btn--open' : ''}`}
            onClick={() => setOpenDropdown(v => v === 'comms' ? null : 'comms')}
          >
            Comms & Reports ▾
          </button>
          {openDropdown === 'comms' && (
            <div className="shared-nav-dropdown">
              <button className="shared-nav-dd-item" onClick={() => go('/fadv-activity')}>📨 FA Activity</button>
              <button className="shared-nav-dd-item" onClick={() => go('/station-map')}>🗺 Maps</button>
              <button className="shared-nav-dd-item" onClick={() => go('/reports')}>📈 Reports</button>
              <button className="shared-nav-dd-item" onClick={() => go('/message-templates')}>✉️ Message Templates</button>
              <button className="shared-nav-dd-item" onClick={() => go('/intro-texts')} style={{ position: 'relative' }}>
                📲 Intro Texts
                {introSmsCount > 0 && (
                  <span className="shared-nav-task-badge">{introSmsCount > 99 ? '99+' : introSmsCount}</span>
                )}
              </button>
              <button className="shared-nav-dd-item" onClick={() => go('/resources')}>📚 Resources</button>
              <button className="shared-nav-dd-item" onClick={() => go('/ai-lab')}>🧪 Mountain AI Lab</button>
              {(isAdmin || isRecruitingAdmin) && (
                <>
                  <div className="shared-nav-dd-divider"></div>
                  <div className="shared-nav-dd-section-label">{isAdmin ? 'Admin' : 'Recruiting Admin'}</div>
                  <button className="shared-nav-dd-item" onClick={() => go('/users')}>
                    👥 Manage Users <span className="shared-nav-admin-dot" />
                  </button>
                  {isAdmin && (
                    <button className="shared-nav-dd-item" onClick={() => go('/blog-agent')}>
                      ✍️ Blog Agent <span className="shared-nav-admin-dot" />
                    </button>
                  )}
                  {canBooks && (
                    <button className="shared-nav-dd-item" onClick={() => go('/books')}>
                      📒 Books <span className="shared-nav-admin-dot" />
                    </button>
                  )}
                </>
              )}
              {isAdmin && (
                <>
                  <div className="shared-nav-dd-divider"></div>
                  <div className="shared-nav-dd-section-label">Deprecated</div>
                  <button className="shared-nav-dd-item" onClick={() => go('/my-clients')}>
                    📞 My Clients <span className="shared-nav-admin-dot" />
                  </button>
                  <button className="shared-nav-dd-item" onClick={() => go('/company-pages')}>
                    🏢 Company Pages <span className="shared-nav-admin-dot" />
                  </button>
                  <button className="shared-nav-dd-item" onClick={() => go('/dashboard')}>
                    <DashboardIcon /> CRM Dashboard <span className="shared-nav-admin-dot" />
                  </button>
                  <button className="shared-nav-dd-item" onClick={() => go('/playbook')}>
                    📝 SOP <span className="shared-nav-admin-dot" />
                  </button>
                  <button className="shared-nav-dd-item" onClick={() => go('/logs')}>
                    📊 Logs <span className="shared-nav-admin-dot" />
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Profile — houses email/role + Logout, replacing the old standalone email text + red button */}
        <div className="shared-nav-dd-wrap">
          <button
            className={`profile-btn${openDropdown === 'profile' ? ' shared-nav-btn--open' : ''}`}
            onClick={() => setOpenDropdown(v => v === 'profile' ? null : 'profile')}
          >
            <div className="profile-avatar">{(user?.email || '?')[0].toUpperCase()}</div>
            <span className="chev">▾</span>
          </button>
          {openDropdown === 'profile' && (
            <div className="shared-nav-dropdown profile-dropdown">
              <div className="profile-dd-header">
                <div className="profile-dd-email">{user?.email}</div>
                <span className="profile-dd-role">{user?.role}</span>
              </div>
              <div className="shared-nav-dd-divider"></div>
              <button className="profile-dd-item" onClick={onLogout}>⎋ Logout</button>
            </div>
          )}
        </div>
      </div>

      {paletteOpen && (
        <div className="palette-overlay" onClick={(e) => { if (e.target.classList.contains('palette-overlay')) closePalette(); }}>
          <div className="palette-modal" onClick={(e) => e.stopPropagation()}>
            <div className="palette-input-row">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
              <input
                ref={paletteInputRef}
                type="text"
                placeholder="Jump to a page…"
                value={paletteQuery}
                onChange={(e) => setPaletteQuery(e.target.value)}
                autoComplete="off"
              />
              <span className="palette-esc">ESC</span>
            </div>
            <div className="palette-results">
              {paletteResults.length === 0 && (
                <div className="palette-empty">No pages match "{paletteQuery}"</div>
              )}
              {(() => {
                let lastGroup = null;
                return paletteResults.map(item => {
                  const showGroup = item.group !== lastGroup;
                  lastGroup = item.group;
                  return (
                    <React.Fragment key={item.path}>
                      {showGroup && <div className="palette-group-label">{item.group}</div>}
                      <div className="palette-item" onClick={() => choosePaletteItem(item.path)}>
                        <div className="palette-item-icon">{item.icon}</div>
                        <div className="palette-item-name">{item.name}</div>
                      </div>
                    </React.Fragment>
                  );
                });
              })()}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
